import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { PowerSettingsNewRounded } from '@mui/icons-material';
import { JsonSchemaInput } from '@ragu-components/json-schema-input';
import { IconBooleanInput, OrdReferenceArrayInput, useFormType, useIsReadonly } from '@ragu-components/ord-components';
import { Resources } from '@ragu-components/ord-resources';
import { dataSchema, LcrRuleUpdateFields } from '@ragu-components/ord-types';
import { get, omit } from 'lodash';
import {
  ArrayInput,
  SimpleFormIterator,
  useGetList,
  useNotify,
  useRefresh,
  useRegisterMutationMiddleware,
  useSourceContext,
  useTranslate,
} from 'react-admin';
import { useFormContext, useWatch } from 'react-hook-form';
import { useParams } from 'react-router-dom';

import { useIngordDataProvider } from 'src/components/flowchart/hooks/useIngordDataProvider';
import { getMatchRulesColors } from 'src/components/flowchart/nodes/Router/RouterControls';
import { OrdDescriptionInput } from 'src/components/formInputs';
import MatchReplaceTestInput from 'src/components/formInputs/MatchReplaceTestInput/MatchReplaceTestInput';

import type { ObjectJsonSchema } from '@ragu-components/json-schema-context';
import type { LcrRuleRead, LcrRuleUpdate } from '@ragu-components/ord-types';
import type { RaRecord, CreateParams } from 'react-admin';
import type { InputColor, SortableInput } from 'src/components/flowchart/dataProvider/types';

type InputColorList = Record<number, InputColor>;
type LcrRuleFormValue = Omit<LcrRuleRead, 'assigned_carriers'> & {
  assigned_carriers: NonNullable<LcrRuleUpdate['assigned_carriers']>;
};
interface LcrsFormValues {
  rules?: LcrRuleFormValue[];
}
type RulesListResponse = LcrRuleRead[] | { data?: LcrRuleRead[] };

const getRulesFromListResponse = (response: RulesListResponse | undefined): LcrRuleRead[] =>
  Array.isArray(response) ? response : (response?.data ?? []);

const toAssignedCarrierIds = (
  assignedCarriers: LcrRuleRead['assigned_carriers']
): NonNullable<LcrRuleUpdate['assigned_carriers']> => assignedCarriers.map(({ id }) => id);

const getRuleIndexFromSource = (source: string): number | null => {
  const sourceParts = source.split('.');
  const rawIndex = sourceParts.at(-2);
  if (rawIndex == null) return null;
  const index = Number(rawIndex);
  return Number.isInteger(index) ? index : null;
};

const toMatchRules = (rules: LcrsFormValues['rules'] = []): Array<{ match_rule: string }> =>
  rules
    .map((rule) => {
      const maybeSnakeCaseRule = rule as LcrRuleFormValue & { match_rule?: string | null };
      return maybeSnakeCaseRule.matchrule ?? maybeSnakeCaseRule.match_rule ?? '';
    })
    .filter((rule) => rule !== '')
    .map((matchRule) => ({ match_rule: matchRule }));

const LcrRuleMatchRuleInput = ({
  matchRulesColors,
  jsonSchema,
}: {
  matchRulesColors: InputColorList;
  jsonSchema: ObjectJsonSchema;
}): React.ReactElement => {
  const { getSource } = useSourceContext();
  const source = getSource(LcrRuleUpdateFields.matchrule);
  const ruleIndex = getRuleIndexFromSource(source);
  const color = ruleIndex != null ? matchRulesColors[ruleIndex] : undefined;
  return (
    <JsonSchemaInput
      source={LcrRuleUpdateFields.matchrule}
      jsonSchema={jsonSchema}
      color={color}
      focused={color !== undefined}
    />
  );
};

/**
 * Input component for LcrsRules.
 * It registers a mutation middleware to update the rules
 *
 * @return {React.ReactElement} The JSX element representing the LcrsRulesInput component
 */
export const LcrsRulesInput = (): React.ReactElement => {
  const { id: parentId } = useParams();
  const { create } = useFormType();
  const readonly = useIsReadonly();
  const lcrRuleJsonSchema = create ? dataSchema.LcrRuleCreate : dataSchema.LcrRuleUpdate;
  const { control, getValues, setValue } = useFormContext<LcrsFormValues>();
  const currentRules = useWatch({ control, name: 'rules' });
  const translate = useTranslate();
  const { data: rulesData } = useGetList<LcrRuleRead>(
    'rules',
    {
      meta: { parentResource: Resources.lcrs, parentId },
      sort: { field: LcrRuleUpdateFields.priority, order: 'ASC' },
    },
    { enabled: !create && parentId != null }
  );
  const defaultRules = useMemo(
    () =>
      getRulesFromListResponse(rulesData as RulesListResponse | undefined).map((rule) => ({
        ...rule,
        assigned_carriers: toAssignedCarrierIds(rule.assigned_carriers),
      })),
    [rulesData]
  );
  const arrayKey = useMemo(
    () =>
      `${parentId ?? 'create'}:${defaultRules.map((rule) => `${rule.id ?? ''}:${rule.matchrule}:${rule.priority}`).join('|')}`,
    [defaultRules, parentId]
  );
  const fetchedRulesSignature = useMemo(
    () => `${parentId ?? 'create'}:${JSON.stringify(defaultRules)}`,
    [defaultRules, parentId]
  );
  const hydratedSignatureRef = useRef<string | null>(null);

  useEffect(() => {
    if (create || parentId == null || rulesData == null) return undefined;
    if (hydratedSignatureRef.current === fetchedRulesSignature) return undefined;

    const options = { shouldDirty: false, shouldTouch: false, shouldValidate: false } as const;
    setValue('rules', defaultRules, options);
    const timeoutId = window.setTimeout(() => {
      setValue('rules', defaultRules, options);
    }, 0);
    hydratedSignatureRef.current = fetchedRulesSignature;

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [create, parentId, rulesData, defaultRules, setValue, fetchedRulesSignature]);

  const [matchRulesColors, setMatchRulesColors] = useState<InputColorList>({});

  const onTestResult = (matchedIndex: number | null): void => {
    const rules = getValues('rules') ?? [];
    const sortableRules: SortableInput[] = rules.map((rule, index) => ({
      id: rule.id ?? String(index),
      value: rule.matchrule,
    }));
    setMatchRulesColors(getMatchRulesColors(matchedIndex, sortableRules));
    setTimeout(() => {
      setMatchRulesColors({});
    }, 3000);
  };

  const refresh = useRefresh();
  useRegisterLcrsRules('rules', refresh);

  return (
    <>
      <ArrayInput
        key={arrayKey}
        source='rules'
        defaultValue={defaultRules}
        sx={{
          '& .RaSimpleFormIterator-form > div': { alignItems: 'flex-start', py: 1 },
          '& .RaArrayInput-label': { marginBottom: 1 },
        }}
      >
        <SimpleFormIterator
          inline
          disableReordering={readonly}
          disableRemove={readonly}
          sx={{
            '& .RaSimpleFormIterator-line': { p: 1, borderBottom: 'unset' },
            '& .RaSimpleFormIterator-inline': { rowGap: 1 },
          }}
        >
          <LcrRuleMatchRuleInput matchRulesColors={matchRulesColors} jsonSchema={lcrRuleJsonSchema} />
          <OrdDescriptionInput source={LcrRuleUpdateFields.description} helperText={false} />
          <OrdReferenceArrayInput
            isRequired
            resource={Resources.assigned_carriers}
            source={LcrRuleUpdateFields.assigned_carriers}
            helperText='resources.lcrs.helperTexts.rules.assigned_carriers'
          />
          <JsonSchemaInput source={LcrRuleUpdateFields.dialingcode} jsonSchema={lcrRuleJsonSchema} helperText={false} />
          <JsonSchemaInput
            source={LcrRuleUpdateFields.priority}
            jsonSchema={lcrRuleJsonSchema}
            sx={{ display: 'none' }}
            defaultValue={0}
          />
          <IconBooleanInput
            label={false}
            helperText={false}
            source={LcrRuleUpdateFields.enabled}
            icon={<PowerSettingsNewRounded />}
          />
        </SimpleFormIterator>
      </ArrayInput>
      <MatchReplaceTestInput
        label={translate('callflows.components.matchReplaceTestInput.testMatchLabel')}
        onTestResult={onTestResult}
        matchRules={toMatchRules(currentRules)}
      />
    </>
  );
};

/** Registers a mutation middleware that creates/deletes Lcrs Rules */
const useRegisterLcrsRules = (source: string, refresh: () => void): void => {
  const notify = useNotify();
  const dataProvider = useIngordDataProvider();
  const memoizedMutationMiddleware = useCallback(
    async (
      resource: string | undefined,
      params: Partial<CreateParams<Partial<RaRecord>>> | undefined,
      next: (
        resource: string | undefined,
        params?: Partial<CreateParams<Partial<RaRecord>>> | undefined
      ) => Promise<unknown>
    ) => {
      if (resource == null) throw new Error('No `resource` param was passed to mutation middleware');
      const { data: formData, ...otherParams } = params ?? { data: undefined };
      const newSourceData: Array<Partial<RaRecord>> = get(formData, source) ?? [];
      const recordData: Partial<RaRecord> = omit(formData, source);

      // add priority field based on index
      const data = newSourceData.map(({ id, ...rest }, index) => ({ ...rest, priority: index }));

      // Perform the main mutation first
      const result = await next(resource, { data: recordData, ...otherParams });

      // After success, create related rules if any
      try {
        const parentData =
          (typeof result === 'object' && result != null && 'data' in result
            ? (result as { data?: RaRecord }).data
            : undefined) ?? (result as RaRecord | undefined);

        if (parentData != null && data.length !== 0) {
          const meta = { parentResource: resource, parentId: parentData.id };
          try {
            await dataProvider.createMany('rules', { data, meta });
          } catch {
            notify('ra-relationships.referenceManyInput.saveError', { type: 'error' });
          }
          // Refresh to invalidate cache and get fresh data
          refresh();
        }
      } catch {
        // Best-effort: if post-mutation work fails, keep base mutation result
      }

      return result;
    },
    [source, dataProvider, notify, refresh]
  );
  useRegisterMutationMiddleware<
    (resource: string | undefined, params?: Partial<CreateParams<Partial<RaRecord>>> | undefined) => Promise<unknown>
  >(memoizedMutationMiddleware);
};
